import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { LockKeyhole } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
    const { session, loading, signIn } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);

    if (!loading && session) return <Navigate to="/dashboard" replace />;

    async function submit(e) {
        e.preventDefault();
        setBusy(true);
        setError("");
        const err = await signIn(email.trim(), password);
        setBusy(false);
        if (err) {
            setError(
                /invalid login credentials/i.test(err)
                    ? "Identifiants incorrects. Vérifiez votre e-mail et votre mot de passe."
                    : /email not confirmed/i.test(err)
                      ? "E-mail non confirmé. Cliquez sur le lien reçu par e-mail ou confirmez l'utilisateur dans Supabase → Authentication → Users."
                      : err
            );
            return;
        }
        navigate("/dashboard", { replace: true });
    }

    return (
        <div data-testid="login-page" className="min-h-screen flex bg-background text-foreground">
            <aside className="hidden lg:flex w-[42%] bg-primary text-primary-foreground flex-col justify-between px-14 py-16">
                <img
                    data-testid="login-brand-logo"
                    src="/logos/logo-acs-blanc.webp"
                    alt="ACS Corporate"
                    className="h-12 w-auto -ml-3 self-start"
                />
                <div>
                    <p className="text-xs uppercase tracking-[0.22em] text-white/40">Data Room</p>
                    <h2 className="mt-4 font-display text-4xl leading-tight">
                        Audit financier &amp; <span className="italic text-white/70">restructuration</span>
                    </h2>
                    <p className="mt-5 max-w-sm text-sm text-white/60">
                        Accès réservé aux collaborateurs du cabinet. Vos dossiers, datarooms et analyses au même endroit.
                    </p>
                </div>
                <p className="text-xs text-white/40">© {new Date().getFullYear()} ACS Corporate</p>
            </aside>

            <main className="flex-1 flex items-center justify-center px-8">
                <form onSubmit={submit} data-testid="login-form" className="w-full max-w-sm space-y-8">
                    <div>
                        <div className="h-11 w-11 rounded-full bg-accent flex items-center justify-center mb-6">
                            <LockKeyhole className="h-5 w-5 text-primary" strokeWidth={1.5} />
                        </div>
                        <h1 className="font-display text-4xl leading-none">Connexion</h1>
                        <p className="mt-3 text-sm text-muted-foreground">
                            Identifiez-vous avec le compte créé par l&apos;administrateur.
                        </p>
                    </div>

                    <div className="space-y-5">
                        <div className="space-y-2">
                            <Label htmlFor="email">Adresse e-mail</Label>
                            <Input
                                id="email"
                                data-testid="login-email-input"
                                type="email"
                                required
                                autoComplete="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="h-11 bg-card"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="password">Mot de passe</Label>
                            <Input
                                id="password"
                                data-testid="login-password-input"
                                type="password"
                                required
                                autoComplete="current-password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="h-11 bg-card"
                            />
                        </div>
                    </div>

                    {error && (
                        <p
                            data-testid="login-error"
                            className="text-sm text-destructive border border-destructive/30 bg-destructive/5 rounded-md px-3 py-2"
                        >
                            {error}
                        </p>
                    )}

                    <Button
                        type="submit"
                        data-testid="login-submit-button"
                        disabled={busy}
                        className="w-full h-11 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground"
                    >
                        {busy ? "Connexion…" : "Se connecter"}
                    </Button>
                </form>
            </main>
        </div>
    );
}
